import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { getProject, getProjectNeighbours } from "@/data/projects";
import { SITE, url } from "@/data/site";
import PageShell from "@/components/PageShell";
import CaseStudyChapters, { CaseStudyMeta } from "@/components/casestudy/CaseStudyChapters";
import CaseStudyPager from "@/components/casestudy/CaseStudyPager";
import { CaseStudyHero, CaseStudyLive } from "@/components/casestudy/CaseStudySections";
import { Overline, Section } from "@/components/shared/Primitives";
import { track, EVENTS } from "@/lib/analytics";

const buildJsonLd = (project, canonical) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CreativeWork",
      name: project.title,
      url: canonical,
      image: project.image,
      dateCreated: project.year,
      description: project.seoDescription,
      creativeWorkStatus: project.status,
      abstract: project.oneLiner,
      keywords: [project.capability, project.tag, "creative technology", "immersive web"].join(", "),
      creator: { "@id": `${SITE.base}/#organization` },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Elentara", item: url("/") },
        { "@type": "ListItem", position: 2, name: "Work", item: url("/work") },
        { "@type": "ListItem", position: 3, name: project.title, item: canonical },
      ],
    },
  ],
});

export default function CaseStudy() {
  const { slug } = useParams();
  const project = getProject(slug);

  useEffect(() => {
    if (project) track(EVENTS.CASE_STUDY_VIEW, { project: project.slug, status: project.status });
  }, [project]);

  const seo = useMemo(() => {
    if (!project) return null;
    const canonical = url(`/work/${project.slug}`);
    return {
      title: project.seoTitle,
      description: project.seoDescription,
      canonical,
      image: project.image,
      jsonLd: buildJsonLd(project, canonical),
    };
  }, [project]);

  if (!project) return <Navigate to="/work" replace />;

  const { prev, next } = getProjectNeighbours(project.slug);
  const isConcept = project.status.startsWith("CONCEPT");

  return (
    <PageShell testId="case-study-page" finalCtaFrom={`case-study:${project.slug}`} seo={seo}>
      <CaseStudyHero project={project} isConcept={isConcept} />

      <Section testId="case-study-body" className="!pt-0">
        <div className="grid gap-12 lg:grid-cols-[2.2fr_1fr] lg:gap-16">
          <CaseStudyChapters project={project} />
          <CaseStudyMeta project={project} />
        </div>

        <CaseStudyLive project={project} isConcept={isConcept} />
        <CaseStudyPager prev={prev} next={next} current={project} />

        <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
          <Link
            to="/work"
            data-testid="case-study-all-work-link"
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#F4F6FB]/50 transition-colors duration-300 hover:text-[#06B6D4]"
          >
            View all work →
          </Link>
          <Overline className="!text-[10px] !tracking-[0.3em] !text-[#F4F6FB]/25">
            {SITE.philosophy}
          </Overline>
        </div>
      </Section>
    </PageShell>
  );
}
